import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";

interface EnhancedAvatarProps {
  src?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  userName?: string;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

// Predefined color palettes for consistent, appealing avatars
const avatarColors = [
  { bg: "bg-blue-500", text: "text-white" },
  { bg: "bg-green-500", text: "text-white" },
  { bg: "bg-purple-500", text: "text-white" },
  { bg: "bg-pink-500", text: "text-white" },
  { bg: "bg-indigo-500", text: "text-white" },
  { bg: "bg-orange-500", text: "text-white" },
  { bg: "bg-teal-500", text: "text-white" },
  { bg: "bg-red-500", text: "text-white" },
  { bg: "bg-yellow-500", text: "text-gray-800" },
  { bg: "bg-cyan-500", text: "text-white" },
];

const sizeClasses = {
  sm: "w-8 h-8 text-sm",
  md: "w-10 h-10 text-base",
  lg: "w-12 h-12 text-lg",
  xl: "w-24 h-24 text-3xl",
};

// Simple hash function to generate consistent color based on initials
const hashCode = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

// Get initials from name or username
const getInitials = (firstName?: string | null, lastName?: string | null, userName?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (userName) {
    const trimmed = userName.trim();
    if (trimmed.includes(' ')) {
      const parts = trimmed.split(' ');
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return trimmed.substring(0, 2).toUpperCase();
  }
  return "?";
};

export const EnhancedAvatar: React.FC<EnhancedAvatarProps> = ({
  src,
  firstName,
  lastName,
  userName,
  size = "md",
  className = "",
}) => {
  const initials = getInitials(firstName, lastName, userName);
  const colorIndex = hashCode(initials) % avatarColors.length;
  const colorScheme = avatarColors[colorIndex];

  return (
    <Avatar className={`${sizeClasses[size]} ${className}`}>
      <AvatarImage src={src || undefined} alt="Profile picture" />
      <AvatarFallback 
        className={`${colorScheme.bg} ${colorScheme.text} font-medium flex items-center justify-center`}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};